import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Check, Circle, Crosshair, ExternalLink, Plus, Trash2, X } from 'lucide-react'
import { Page, PageHeader, Card, CardHeader, btnPrimary, btnSecondary, btnGhost, SkeletonRows } from '@/components/rm'
import { Field, Input, Select, Textarea } from '@/components/rm/form'
import { PlacePicker } from '@/components/place-picker'
import { useAuth } from '@/hooks/use-auth'
import { peso } from '@/lib/ph'
import { BRAND_DOMAIN } from '@/components/brand'
import { useCategories, useListing, placeLabel, type ListingService, type Missing } from '@/lib/listing'
import { cn, errorText } from '@/lib/utils'
import { devicePoint } from '@/lib/search'
import { VerifyBanner } from '@/components/verify-banner'

// Spec 003. The trade business's public listing: what clients see, and where it works. A business
// tool, so English first; the public page at /p/$slug is bilingual.
export const Route = createFileRoute('/_app/listing')({ component: ListingPage })

const MISSING_LABEL: Record<Missing, string> = {
  headline: 'A headline',
  category: 'At least one category',
  base_city: 'Your home base',
  area: 'The places you serve',
  service: 'At least one service shown on the listing',
}
const RADII = [0, 5, 10, 20, 30, 50, 100]

function ListingPage() {
  const { role } = useAuth()
  const canEdit = role === 'company-admin'
  const { listing, saveDetails, toggleCategory, addArea, removeArea, saveService, deleteService, publish, unpublish, setPin } = useListing()
  const cats = useCategories()
  const d = listing.data
  const l = d?.listing

  const [headline, setHeadline] = useState('')
  const [about, setAbout] = useState('')
  const [years, setYears] = useState('')
  useEffect(() => {
    if (!d) return
    setHeadline(l?.headline ?? ''); setAbout(l?.about ?? ''); setYears(l?.years_experience?.toString() ?? '')
  }, [d, l])

  const details = (patch: Partial<{ base_city_code: string | null; radius_km: number | null }> = {}) => ({
    headline: headline.trim(), about: about.trim(),
    years_experience: years === '' ? null : Math.max(0, Math.min(80, Number(years) || 0)),
    base_city_code: l?.base_city_code ?? null, radius_km: l?.radius_km ?? null, ...patch,
  })
  const run = async (p: Promise<unknown>, ok?: string) => {
    try { await p; if (ok) toast.success(ok) } catch (e) { toast.error(errorText(e, 'Could not save')) }
  }

  if (listing.isLoading || !d) return <Page><PageHeader title="My listing" /><Card className="mt-5"><SkeletonRows /></Card></Page>

  const publicUrl = l?.slug ? `${window.location.origin}/p/${l.slug}` : null

  return (
    <Page>
      <PageHeader title="My listing" subtitle="What clients see when they look for a trade person near them." />
      <VerifyBanner what="listing" />

      <Card className="mt-5 p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="flex items-center gap-2 text-[15px] font-bold text-token-primary">
              <span className={cn('inline-block h-2.5 w-2.5 rounded-full', l?.published ? 'bg-token-success' : 'bg-token-border')} />
              {l?.published ? 'Published' : 'Not published'}
            </p>
            {l?.published && publicUrl && (
              <a href={publicUrl} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-[13px] font-semibold text-brand hover:underline">
                {BRAND_DOMAIN}/p/{l.slug} <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
            {!l?.published && (
              d.missing.length
                ? <ul className="mt-2 space-y-1 text-[13px] text-token-secondary">
                    {(['headline', 'category', 'base_city', 'area', 'service'] as Missing[]).map((m) => (
                      <li key={m} className="flex items-center gap-2">
                        {d.missing.includes(m) ? <Circle className="h-3.5 w-3.5 text-token-muted" /> : <Check className="h-3.5 w-3.5 text-token-success" />}
                        <span className={d.missing.includes(m) ? '' : 'text-token-muted line-through'}>{MISSING_LABEL[m]}</span>
                      </li>
                    ))}
                  </ul>
                : <p className="mt-1 text-[13px] text-token-secondary">Ready to publish.</p>
            )}
          </div>
          {canEdit && (l?.published
            ? <button className={btnSecondary} onClick={() => run(unpublish.mutateAsync(), 'Listing unpublished')}>Unpublish</button>
            : <button className={btnPrimary} disabled={publish.isPending} onClick={async () => {
                const r = await publish.mutateAsync().catch((e) => { toast.error(e.message); return null })
                if (r?.ok) toast.success('Listing published')
                else if (r) toast.error('Not ready yet: ' + (r.missing ?? []).map((m) => MISSING_LABEL[m].toLowerCase()).join(', '))
              }}>Publish</button>)}
        </div>
      </Card>

      <Card className="mt-5">
        <CardHeader title="About your business" />
        <div className="grid gap-4 p-5 sm:grid-cols-2">
          <Field label="Headline" className="sm:col-span-2">
            <Input value={headline} maxLength={80} disabled={!canEdit} onChange={(e) => setHeadline(e.target.value)} placeholder="Aircon cleaning and repair in eastern Metro Manila" />
          </Field>
          <Field label="About" className="sm:col-span-2">
            <Textarea rows={4} value={about} maxLength={1000} disabled={!canEdit} onChange={(e) => setAbout(e.target.value)} placeholder="What you do, how long you have done it, and what clients can expect." />
          </Field>
          <Field label="Years of experience">
            <Input type="number" min={0} max={80} value={years} disabled={!canEdit} onChange={(e) => setYears(e.target.value)} />
          </Field>
        </div>
        {canEdit && (
          <div className="flex justify-end border-t border-token-subtle px-5 py-3">
            <button className={btnPrimary} disabled={saveDetails.isPending} onClick={() => run(saveDetails.mutateAsync(details()), 'Saved')}>Save</button>
          </div>
        )}
      </Card>

      <Card className="mt-5">
        <CardHeader title="What you do" />
        <div className="flex flex-wrap gap-2 p-5">
          {(cats.data ?? []).map((c) => {
            const on = d.categories.includes(c.slug)
            return (
              <button key={c.slug} type="button" disabled={!canEdit || toggleCategory.isPending} aria-pressed={on}
                onClick={() => run(toggleCategory.mutateAsync({ slug: c.slug, on: !on }))}
                className={cn('rounded-full border px-3.5 py-1.5 text-[13px] font-semibold transition-colors',
                  on ? 'border-brand bg-token-selected text-brand' : 'border-token-subtle text-token-secondary hover:text-token-primary')}>
                {on && <Check className="mr-1 inline h-3.5 w-3.5" />}{c.name_en}
              </button>
            )
          })}
        </div>
      </Card>

      <Card className="mt-5">
        <CardHeader title="Where you work" />
        <div className="space-y-5 p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Home base">
              {d.base
                ? <div className="flex h-10 items-center justify-between rounded-[10px] border border-token-subtle px-3 text-[14px] text-token-primary">
                    {placeLabel(d.base)}
                    {canEdit && <button type="button" className="text-token-muted hover:text-token-primary" aria-label="Change home base"
                      onClick={() => run(saveDetails.mutateAsync(details({ base_city_code: null, radius_km: null })))}><X className="h-4 w-4" /></button>}
                  </div>
                : canEdit
                  ? <PlacePicker placeholder="Search your city or municipality" onPick={(p) => run(saveDetails.mutateAsync(details({ base_city_code: p.code })), 'Home base set')} />
                  : <p className="text-[13px] text-token-muted">Not set</p>}
            </Field>
            <Field label="Also within (from your home base)">
              <Select value={String(l?.radius_km ?? 0)} disabled={!canEdit || !d.base}
                onChange={(e) => run(saveDetails.mutateAsync(details({ radius_km: Number(e.target.value) || null })))}>
                {RADII.map((r) => <option key={r} value={r}>{r ? `${r} km` : 'No radius'}</option>)}
              </Select>
            </Field>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-[10px] border border-token-subtle px-4 py-3">
            <div>
              <p className="text-[13.5px] font-semibold text-token-primary">Base pin {l?.base_point ? '· set' : '· not set'}</p>
              <p className="text-[12.5px] text-token-secondary">Lets clients nearby see how far you are, and makes your radius work. Clients see the distance, never the pin.</p>
            </div>
            {canEdit && <div className="flex gap-2">
              <button type="button" className={btnSecondary} disabled={setPin.isPending} onClick={async () => {
                const p = await devicePoint()
                if (!p) return toast.error('Could not get this device’s location. Allow location access and try again.')
                await run(setPin.mutateAsync(p), 'Base pin set from this device')
              }}><Crosshair className="h-4 w-4" /> Use this device’s location</button>
              {l?.base_point && <button type="button" className={btnGhost} onClick={() => run(setPin.mutateAsync(null), 'Base pin cleared')}>Clear</button>}
            </div>}
          </div>
          <div>
            <p className="mb-2 text-[13px] font-semibold text-token-secondary">Cities and municipalities you serve</p>
            <div className="mb-3 flex flex-wrap gap-2">
              {d.area.length === 0 && <span className="text-[13px] text-token-muted">None yet.</span>}
              {d.area.map((p) => (
                <span key={p.code} className="inline-flex items-center gap-1.5 rounded-full bg-token-selected px-3 py-1 text-[13px] font-semibold text-token-primary">
                  {placeLabel(p)}
                  {canEdit && <button type="button" aria-label={`Remove ${p.name}`} onClick={() => run(removeArea.mutateAsync(p.code))}><X className="h-3.5 w-3.5" /></button>}
                </span>
              ))}
            </div>
            {canEdit && <PlacePicker className="max-w-md" placeholder="Add a city or municipality" exclude={d.area.map((p) => p.code)} onPick={(p) => run(addArea.mutateAsync(p.code))} />}
          </div>
        </div>
      </Card>

      <ServicesCard services={d.services} canEdit={canEdit} categories={cats.data ?? []}
        onSave={(s) => run(saveService.mutateAsync(s), 'Service saved')} onDelete={(id) => run(deleteService.mutateAsync(id), 'Service removed')} />
    </Page>
  )
}

type Draft = { id?: string; name: string; category_slug: string; price: string; duration_min: string; marketplace: boolean; description: string }
const blank: Draft = { name: '', category_slug: '', price: '', duration_min: '60', marketplace: true, description: '' }

function ServicesCard({ services, canEdit, categories, onSave, onDelete }: {
  services: ListingService[]; canEdit: boolean; categories: Array<{ slug: string; name_en: string }>
  onSave: (s: Partial<ListingService> & { name: string }) => Promise<void>; onDelete: (id: string) => Promise<void>
}) {
  const [draft, setDraft] = useState<Draft | null>(null)
  const catName = (slug: string | null) => categories.find((c) => c.slug === slug)?.name_en ?? 'No category'
  const save = async () => {
    if (!draft || !draft.name.trim()) return
    await onSave({
      id: draft.id, name: draft.name, category_slug: draft.category_slug || null, description: draft.description.trim() || null,
      price: draft.price === '' ? null : Math.max(0, Number(draft.price) || 0),
      duration_min: Math.max(15, Number(draft.duration_min) || 60), marketplace: draft.marketplace,
    })
    setDraft(null)
  }
  return (
    <Card className="mt-5">
      <CardHeader title="Services" action={canEdit && !draft ? <button className={btnGhost} onClick={() => setDraft(blank)}><Plus className="h-4 w-4" /> Add service</button> : undefined} />
      <div className="divide-y divide-token-subtle">
        {services.length === 0 && !draft && <p className="px-5 py-4 text-[13px] text-token-muted">No services yet. Add the jobs clients can book, with a fixed price or "ask for a quote".</p>}
        {services.map((s) => (
          <div key={s.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
            <div className="min-w-0 flex-1">
              <p className="truncate text-[14px] font-semibold text-token-primary">{s.name}</p>
              <p className="text-[12.5px] text-token-secondary">{catName(s.category_slug)} · {s.duration_min} min{s.marketplace ? '' : ' · not on listing'}{s.active ? '' : ' · inactive'}</p>
            </div>
            <span className="text-[14px] font-semibold tabular-nums text-token-primary">{s.price == null ? 'Ask for a quote' : peso(s.price)}</span>
            {canEdit && <>
              <button className={btnGhost} onClick={() => setDraft({ id: s.id, name: s.name, category_slug: s.category_slug ?? '', price: s.price?.toString() ?? '', duration_min: String(s.duration_min), marketplace: s.marketplace, description: s.description ?? '' })}>Edit</button>
              <button className={btnGhost} aria-label={`Delete ${s.name}`} onClick={() => onDelete(s.id)}><Trash2 className="h-4 w-4" /></button>
            </>}
          </div>
        ))}
        {draft && (
          <div className="grid gap-3 bg-token-surface px-5 py-4 sm:grid-cols-2">
            <Field label="Service name" className="sm:col-span-2"><Input value={draft.name} maxLength={120} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Split-type aircon cleaning" /></Field>
            <Field label="Category">
              <Select value={draft.category_slug} onChange={(e) => setDraft({ ...draft, category_slug: e.target.value })}>
                <option value="">Choose…</option>
                {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name_en}</option>)}
              </Select>
            </Field>
            <Field label="Price in pesos (leave empty for “ask for a quote”)"><Input type="number" min={0} step={50} value={draft.price} onChange={(e) => setDraft({ ...draft, price: e.target.value })} placeholder="1200" /></Field>
            <Field label="Duration (minutes)"><Input type="number" min={15} step={15} value={draft.duration_min} onChange={(e) => setDraft({ ...draft, duration_min: e.target.value })} /></Field>
            <label className="flex items-center gap-2 self-end pb-2 text-[13.5px] text-token-primary">
              <input type="checkbox" className="h-4 w-4 accent-brand" checked={draft.marketplace} onChange={(e) => setDraft({ ...draft, marketplace: e.target.checked })} /> Show on my listing
            </label>
            <Field label="Description (optional)" className="sm:col-span-2"><Textarea rows={2} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></Field>
            <div className="flex justify-end gap-2 sm:col-span-2">
              <button className={btnSecondary} onClick={() => setDraft(null)}>Cancel</button>
              <button className={btnPrimary} disabled={!draft.name.trim()} onClick={save}>Save service</button>
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}
